import { useEffect, useState } from 'react'
import { Controller, useFormContext } from '@astro/forms'

function ImagePreview({ file }) {
  // Object URL is created inside the effect so StrictMode's mount→unmount→mount
  // cycle can't revoke the URL the <img> is still using.
  const [url, setUrl] = useState(null)
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  if (!url) return null
  return (
    <img
      src={url}
      alt="Profile preview"
      className="w-20 h-20 rounded-full object-cover mx-auto mb-2 border-2 border-goldPrimary/40 shadow-md"
    />
  )
}

/**
 * Dashed drop-zone file picker bound to the form. Validates size (and that a
 * file is present when required). Set `preview` for a round image preview.
 */
export default function FileDropField({
  name,
  label,
  hint,
  accept,
  maxSizeMb,
  requiredMessage,
  icon = '📎',
  prompt,
  preview = false,
}) {
  const { control } = useFormContext()
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (file) => {
          if (!file) return requiredMessage ?? true
          if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) return `File must be under ${maxSizeMb}MB.`
          return true
        },
      }}
      render={({ field, fieldState }) => (
        <div>
          {label && (
            <span className="ar-label">
              {label}
              {hint && <span className="normal-case font-normal text-stone-400"> {hint}</span>}
            </span>
          )}
          <label className={`ar-file-drop${field.value ? ' has-file' : ''}`}>
            <input
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => field.onChange(e.target.files?.[0] ?? null)}
            />
            {preview && field.value && <ImagePreview file={field.value} />}
            <span className="text-[12px] font-semibold text-stone-500 block truncate">
              {field.value ? `✓ ${field.value.name}` : `${icon} ${prompt}`}
            </span>
          </label>
          {fieldState.error && <p className="ar-error">{fieldState.error.message}</p>}
        </div>
      )}
    />
  )
}
